import AddressLookup from '@acorn-portal/shared/src/components/addressLookup';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import DatePickerField from '@acorn-portal/shared/src/components/datePicker/DropDownDatePicker';
import ModificationForm from '@acorn-portal/shared/src/components/forms/ModificationForm';
import AutoCompleteFormField from '@acorn-portal/shared/src/components/inputs/AutoCompleteFormField';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import {
	PcRestartResponse,
	PrivateCarClient,
	UpdatePrivateCarQuoteCommand,
} from '@acorn-portal/shared/src/services/subscriptionAPI';
import { updateSubscriptionData } from '@acorn-portal/shared/src/store/subscription.slice';
import { updateChangeOfVehicle } from '@acorn-portal/shared/src/store/subscriptionChanges.slice';
import {
	COVER_LEVEL_RESTART,
	ESTIMATED_VALUE_MODAL,
	OVERNIGHT_ADDRESS_DIFFERENT,
	VEHICLE_LEFT_HAND_DRIVE,
} from '@acorn-portal/shared/src/utils/modalText';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { Box, Collapse } from '@mui/material';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { amendVehiclePart2Schema } from '../validation/amend-vehicle-part2-schema';
import { getVehicleDetails, LocationProps } from './utils/get-vehicle-details';
import { getVehicleInitialValues, IAmendVehiclePart2 } from './utils/get-vehicle-initial-values';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

const AmendVehiclePart2 = () => {
	const [open, toggleModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const lists = useAppSelector((state) => state.referenceData);
	const state = useAppSelector((state) => state.subscription);
	const dispatch = useAppDispatch();
	const location = useLocation() as unknown as LocationProps;
	const vehicleLookup = useAppSelector((state) => state.vehicleLookup);
	const { vehicleInfo } = vehicleLookup;
	const { quoteData } = useAppSelector((state) => state.subscription);
	const coverDetails = quoteData?.quoteDetails.riskData.coverDetails;
	const initialValues = getVehicleInitialValues();

	const handleOnSubmit = async (values: IAmendVehiclePart2) => {
		setLoading(true);

		try {
			if (!vehicleInfo) throw new Error('Missing vehicle information');

			const vehicleDetails = getVehicleDetails(
				values,
				location,
				vehicleInfo,
				state.quoteData?.quoteDetails.riskData.proposerDetails.address,
				state.quoteData?.quoteDetails.riskData.vehicleDetails.vehicleUse,
				state.quoteData?.quoteDetails.riskData.vehicleDetails.annualMileage,
			);

			if (!coverDetails) throw new Error('Missing cover details');

			const url = import.meta.env.VITE_SUBSCRIPTION_API;
			const subscriptionClient = new PrivateCarClient(url, API);

			const body: UpdatePrivateCarQuoteCommand = {
				quoteReference: quoteData?.quoteResponse.quoteRef ?? '',
				quoteToken: quoteData?.quoteToken ?? '',
				quoteDetails: {
					...quoteData?.quoteDetails,
					riskData: {
						...quoteData?.quoteDetails.riskData,
						vehicleDetails,
						coverDetails: {
							...coverDetails,
							levelOfCover: values.levelOfCover,
						},
					},
				},
			};
			const result: PcRestartResponse = await subscriptionClient.updateQuote(body);

			if (result.quoteResponse.underwrittenOptions?.[0]?.status === 'Declined') {
				navigate(ROUTE_PATHS.subscriptionRestartReferral, { replace: true });
				return;
			}

			dispatch(
				updateSubscriptionData({
					policyToken: state.policyToken ?? '',
					effectiveDate: state.effectiveDate ?? '',
					quoteData: result,
					sendWelcomePackStatus: undefined,
					autoRenew: undefined,
				}),
			);

			dispatch(updateChangeOfVehicle(true));

			navigate(`${ROUTE_PATHS.subscriptionRestartSummary}/${state.policyToken}`);
		} catch (error) {
			console.error('error', error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	return (
		<JourneyCardLayout pageTitle='New Vehicle Details' loading={loading}>
			<Formik
				initialValues={initialValues}
				onSubmit={handleOnSubmit}
				validationSchema={amendVehiclePart2Schema(vehicleLookup.vehicleInfo?.year ?? 0)}
			>
				{(formik) => (
					<Form>
						<FormikLogErrors />
						<AutoCompleteFormField
							id='ownership'
							name='owner'
							label='Who currently owns the vehicle?'
							value={formik.values.owner}
							onChange={(value) => formik.setFieldValue('owner', value)}
							options={lists.vehicleOwnershipOptions}
							error={formik.touched.owner && Boolean(formik.errors.owner)}
							helperText={formik.touched.owner && formik.errors.owner}
						/>
						<AutoCompleteFormField
							id='registeredKeeper'
							name='registeredKeeper'
							label='Who is the registered keeper of the vehicle?'
							value={formik.values.registeredKeeper}
							onChange={(value) => formik.setFieldValue('registeredKeeper', value)}
							options={lists.vehicleKeeperOptions}
							error={formik.touched.registeredKeeper && Boolean(formik.errors.registeredKeeper)}
							helperText={formik.touched.registeredKeeper && formik.errors.registeredKeeper}
						/>
						<RadioGroupButton
							id='hasModifications'
							name='hasModifications'
							label='Has the vehicle been modified?'
							value={formik.values.hasModifications}
							onChange={(value) => formik.setFieldValue('hasModifications', value)}
							error={
								(formik.touched.hasModifications && Boolean(formik.errors.hasModifications)) ||
								(formik.touched.hasModifications && Boolean(formik.errors.modifications))
							}
							helperText={
								(formik.touched.hasModifications && formik.errors.hasModifications) ||
								(formik.touched.modifications && (formik.errors.modifications as string))
							}
						/>
						<Collapse in={formik.values.hasModifications === 'yes'}>
							<Box>
								<ModificationForm
									hasGlobalError={formik.touched.modifications && Boolean(formik.errors.modifications)}
									onChange={(value) => formik.setFieldValue('modifications', value)}
									value={formik.values.modifications}
								/>
							</Box>
						</Collapse>
						<RadioGroupButton
							id='imported'
							name='imported'
							label='Is the vehicle imported?'
							value={formik.values.imported}
							onChange={(value) => formik.setFieldValue('imported', value)}
							error={formik.touched.imported && Boolean(formik.errors.imported)}
							helperText={formik.touched.imported && formik.errors.imported}
						/>
						<Collapse in={formik.values.imported === 'yes'}>
							<Box>
								<AutoCompleteFormField
									id='countryOfRegistration'
									name='countryOfRegistration'
									label='Initial country of registration'
									value={formik.values.countryOfRegistration}
									onChange={(value) => formik.setFieldValue('countryOfRegistration', value)}
									options={lists.countryEarnedNcdOptions}
									error={formik.touched.countryOfRegistration && Boolean(formik.errors.countryOfRegistration)}
									helperText={formik.touched.countryOfRegistration && formik.errors.countryOfRegistration}
								/>
							</Box>
						</Collapse>
						<RadioGroupButton
							id='isLeftHandDrive'
							name='isLeftHandDrive'
							label='Is the vehicle left-hand drive?'
							value={formik.values.isLeftHandDrive}
							onChange={(value) => formik.setFieldValue('isLeftHandDrive', value)}
							error={formik.touched.isLeftHandDrive && Boolean(formik.errors.isLeftHandDrive)}
							helperText={formik.touched.isLeftHandDrive && formik.errors.isLeftHandDrive}
							modal={{
								title: VEHICLE_LEFT_HAND_DRIVE.header,
								description: VEHICLE_LEFT_HAND_DRIVE.text,
							}}
						/>
						<DatePickerField
							id='purchasedDate'
							name='purchasedDate'
							day={false}
							label='Purchased date'
							value={formik.values.purchasedDate}
							onChange={(value) => formik.setFieldValue('purchasedDate', value)}
							error={
								(formik.touched.purchasedDate?.month && Boolean(formik.errors.purchasedDate?.month)) ||
								(formik.touched.purchasedDate?.year && Boolean(formik.errors.purchasedDate?.year))
							}
							helperText={
								(formik.touched.purchasedDate?.month && formik.errors.purchasedDate?.month) ||
								(formik.touched.purchasedDate?.year && formik.errors.purchasedDate?.year)
							}
						/>
						<ToggleButtonFormField
							id='keptOvernight'
							name='keptOvernight'
							label='Where is the vehicle kept overnight?'
							value={formik.values.keptOvernight}
							onChange={(value) => formik.setFieldValue('keptOvernight', value)}
							options={lists.vehicleStorageOptions as IOptions[]}
							error={formik.touched.keptOvernight && Boolean(formik.errors.keptOvernight)}
							helperText={formik.touched.keptOvernight && formik.errors.keptOvernight}
						/>
						<RadioGroupButton
							id='overnightAddressIsAway'
							name='overnightAddressIsAway'
							label='Is your home address different to where your vehicle is kept overnight?'
							value={formik.values.overnightAddressIsAway}
							onChange={(value) => {
								formik.setFieldValue('overnightAddressIsAway', value);
								formik.setFormikState((x) => ({
									...x,
									values: {
										...x.values,
										overnightAddress: {
											line1: '',
											line2: undefined,
											line3: undefined,
											line4: undefined,
											city: '',
											postcode: '',
										},
									},
								}));
							}}
							error={formik.touched.overnightAddressIsAway && Boolean(formik.errors.overnightAddressIsAway)}
							helperText={formik.touched.overnightAddressIsAway && formik.errors.overnightAddressIsAway}
							modal={{
								title: OVERNIGHT_ADDRESS_DIFFERENT.title,
								description: OVERNIGHT_ADDRESS_DIFFERENT.description,
							}}
						/>
						<Collapse in={formik.values.overnightAddressIsAway === 'yes'}>
							<AddressLookup
								initialValue={formik.values.overnightAddress}
								firstFieldLabel='Overnight house number or name'
								postcodeLabel='Overnight Postcode'
								onChange={(value) => {
									formik.setFormikState((formState) => ({
										...formState,
										values: {
											...formState.values,
											overnightAddress: {
												...formState.values.overnightAddress,
												line1: value?.line1 ?? '',
												line2: value?.line2,
												line3: value?.line3,
												line4: value?.line4,
												city: value?.city ?? '',
												county: value?.county ?? '',
												country: value?.country ?? '',
												postcode: value?.postcode ?? '',
											},
										},
									}));
								}}
							/>
						</Collapse>
						<FormTextField
							id='value'
							name='value'
							label='Estimated value (£)'
							value={formik.values.value}
							onChange={formik.handleChange}
							error={formik.touched.value && Boolean(formik.errors.value)}
							helperText={formik.touched.value && formik.errors.value}
							format='currency'
							onBlur={formik.handleBlur}
							modal={{
								title: ESTIMATED_VALUE_MODAL.header,
								description: ESTIMATED_VALUE_MODAL.text,
							}}
						/>
						<SelectFormField
							label='Cover required'
							name='levelOfCover'
							id='levelOfCover'
							value={formik.values.levelOfCover}
							onChange={(value) => formik.setFieldValue('levelOfCover', value)}
							options={lists.levelOfCoverTypes.filter((x) => x.value !== 'TPO') as IOptions[]}
							error={formik.touched.levelOfCover && Boolean(formik.errors.levelOfCover)}
							helperText={formik.touched.levelOfCover && formik.errors.levelOfCover}
							modal={{
								title: COVER_LEVEL_RESTART.header,
								description: COVER_LEVEL_RESTART.text,
							}}
						/>
						<ActionButtons>
							<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
							<ContinueButton type='submit'>Continue</ContinueButton>
						</ActionButtons>
						<HelperModal
							open={open}
							onClose={() => toggleModal(false)}
							title='Discard The Changes?'
							description='Are you sure you wish to discard the changes?'
							closeText='No'
							submitText='Yes'
							onSubmit={() => {
								formik.resetForm();
								navigate(-1);
							}}
						/>
					</Form>
				)}
			</Formik>
		</JourneyCardLayout>
	);
};

export default AmendVehiclePart2;
